import { useEffect, useState } from "react"
import { useLocation } from "react-router"
import { getCurrentMaintenance, type Maintenance } from "~/lib/maintenance"

const STORAGE_PREFIX = "maintenance-notice-closed:"
const PREVIEW_KEY = "maintenance-notice-preview"

/**
 * The scheduled maintenance notice for today (see lib/maintenance.ts), and
 * whether this browser has closed it.
 *
 * `?maintenance=on` previews it early, to review it. The preview sticks
 * while clicking around for the rest of the browser session, and opening the
 * link again brings a closed bar back. `?maintenance=off` ends it.
 */
export function useMaintenanceNotice(): {
  maintenance: Maintenance | null
  visible: boolean
  dismiss: () => void
} {
  const { search } = useLocation()
  const param = new URLSearchParams(search).get("maintenance")
  const fromLink = param === "on"
  const [storedPreview, setStoredPreview] = useState(false)
  const maintenance = getCurrentMaintenance(
    undefined,
    param !== "off" && (fromLink || storedPreview),
  )
  const id = maintenance?.id
  const [closed, setClosed] = useState(false)

  useEffect(() => {
    try {
      if (param === "off") sessionStorage.removeItem(PREVIEW_KEY)
      else if (param === "on") sessionStorage.setItem(PREVIEW_KEY, "on")
      setStoredPreview(
        param !== "off" && sessionStorage.getItem(PREVIEW_KEY) === "on",
      )
    } catch {
      // Storage blocked: the preview lasts for this page only.
    }
  }, [param])

  useEffect(() => {
    if (!id) return
    try {
      // Opening a preview link shows the bar again even if it was closed.
      if (fromLink) localStorage.removeItem(STORAGE_PREFIX + id)
      setClosed(!!localStorage.getItem(STORAGE_PREFIX + id))
    } catch {
      setClosed(false)
    }
  }, [id, fromLink])

  const dismiss = () => {
    setClosed(true)
    if (!id) return
    try {
      localStorage.setItem(STORAGE_PREFIX + id, new Date().toISOString())
    } catch {
      // Storage blocked: the bar stays closed for this visit only.
    }
  }

  return { maintenance, visible: !!maintenance && !closed, dismiss }
}
